// A tiny expression language, evaluated once per feature.
//
// `GI.SUMEACH("area * hardCost / levels")` has to run its expression against
// every section and add up the results. Parsing it per feature would be absurd,
// so it is compiled ONCE into a closure over a variable lookup and then called
// in a loop.
//
// Numbers, field names, `+ - * / ^ %`, comparisons (`== != > < >= <=`), logical
// `and`/`or`/`not`, a `cond ? a : b` ternary, parentheses, unary minus, and a
// handful of functions — the same shape as a real Giraffe analytics measure's
// own `formula` field (expr-eval-flavoured), which is the one other place this
// grammar has to round-trip through: a custom measure materialised onto the
// Measures tab is parsed with this, and read back from Giraffe's component-
// letter formula shape through it too. Anything richer belongs in the sheet,
// where the user already has four hundred functions.
//
// No real boolean type: a comparison or a logical operator yields 1 or 0, and
// every truth test (`IF`, `not`, the ternary's condition) asks "> 0" — the same
// convention IF already used, now applied uniformly.

export type Vars = (name: string) => number | null;

export interface Compiled {
  /** Null when a field the expression needs is missing on this feature. */
  (vars: Vars): number | null;
  /** Every field name the expression reads, for diagnostics. */
  fields: string[];
}

export class ExpressionError extends Error {}

// ---------------------------------------------------------------------------
// Tokens
// ---------------------------------------------------------------------------

type Token =
  | { kind: 'number'; value: number }
  | { kind: 'name'; value: string }
  | { kind: 'op'; value: string }
  | { kind: 'paren'; value: '(' | ')' }
  | { kind: 'comma' };

/**
 * A field can be written bare (`area`) or bracketed (`[dwelling-1BED-Count]`).
 *
 * The brackets are not decoration: a real dwelling-mix field is
 * `dwelling-1BED-Count`, and read bare that is `dwelling` minus `1BED` minus
 * `Count` — three unknown names and a wrong answer rather than an error.
 */
function tokenize(src: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const ch = src[i]!;

    if (/\s/.test(ch)) {
      i += 1;
      continue;
    }

    if (ch === '[') {
      const end = src.indexOf(']', i);
      if (end < 0) throw new ExpressionError('A [field name] is missing its closing bracket.');
      out.push({ kind: 'name', value: src.slice(i + 1, end).trim() });
      i = end + 1;
      continue;
    }

    if (/[0-9.]/.test(ch)) {
      const m = /^[0-9]*\.?[0-9]+([eE][+-]?[0-9]+)?/.exec(src.slice(i));
      if (!m) throw new ExpressionError(`Cannot read the number at "${src.slice(i, i + 8)}".`);
      out.push({ kind: 'number', value: Number(m[0]) });
      i += m[0].length;
      continue;
    }

    if (/[A-Za-z_]/.test(ch)) {
      const m = /^[A-Za-z_][A-Za-z0-9_.]*/.exec(src.slice(i))!;
      out.push({ kind: 'name', value: m[0] });
      i += m[0].length;
      continue;
    }

    if (ch === '(' || ch === ')') {
      out.push({ kind: 'paren', value: ch });
      i += 1;
      continue;
    }

    if (ch === ',') {
      out.push({ kind: 'comma' });
      i += 1;
      continue;
    }

    if ('+-*/^%'.includes(ch)) {
      out.push({ kind: 'op', value: ch });
      i += 1;
      continue;
    }

    if (ch === '=' && src[i + 1] === '=') {
      out.push({ kind: 'op', value: '==' });
      i += 2;
      continue;
    }
    if (ch === '!' && src[i + 1] === '=') {
      out.push({ kind: 'op', value: '!=' });
      i += 2;
      continue;
    }
    if (ch === '>' || ch === '<') {
      const withEquals = src[i + 1] === '=';
      out.push({ kind: 'op', value: withEquals ? `${ch}=` : ch });
      i += withEquals ? 2 : 1;
      continue;
    }
    if (ch === '?' || ch === ':') {
      out.push({ kind: 'op', value: ch });
      i += 1;
      continue;
    }

    throw new ExpressionError(`"${ch}" is not something this expression understands.`);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Parser (precedence climbing)
// ---------------------------------------------------------------------------

type Node = (vars: Vars) => number | null;

type BinaryOp = { precedence: number; rightAssoc?: boolean; apply: (a: number, b: number) => number };

const truthy = (n: number): boolean => n > 0;
const bool = (b: boolean): number => (b ? 1 : 0);

// Lowest binds loosest: or, and, comparisons, + -, * / %, ^ — the usual
// arithmetic ladder with logic stacked below it, the same order a real
// Giraffe analytics formula's own grammar uses.
const BINARY: Record<string, BinaryOp> = {
  '+': { precedence: 4, apply: (a, b) => a + b },
  '-': { precedence: 4, apply: (a, b) => a - b },
  '*': { precedence: 5, apply: (a, b) => a * b },
  '/': { precedence: 5, apply: (a, b) => a / b },
  '%': { precedence: 5, apply: (a, b) => a % b },
  // Left-associative, matching Excel: 2^3^2 is 64, not 512.
  '^': { precedence: 6, apply: (a, b) => a ** b },
  '==': { precedence: 3, apply: (a, b) => bool(a === b) },
  '!=': { precedence: 3, apply: (a, b) => bool(a !== b) },
  '>': { precedence: 3, apply: (a, b) => bool(a > b) },
  '<': { precedence: 3, apply: (a, b) => bool(a < b) },
  '>=': { precedence: 3, apply: (a, b) => bool(a >= b) },
  '<=': { precedence: 3, apply: (a, b) => bool(a <= b) },
};

/**
 * `and`/`or`, spelled as words rather than symbols — tokenized as ordinary
 * names (`and` is no different from `area` to the tokenizer) and recognised
 * here instead, which is also why a field actually named "and" or "or" cannot
 * be read by this language. An acceptable trade for two reserved words.
 */
const WORD_BINARY: Record<string, BinaryOp> = {
  or: { precedence: 1, apply: (a, b) => bool(truthy(a) || truthy(b)) },
  and: { precedence: 2, apply: (a, b) => bool(truthy(a) && truthy(b)) },
};

function binaryOpAt(token: Token | undefined): BinaryOp | undefined {
  if (!token) return undefined;
  if (token.kind === 'op') return BINARY[token.value];
  if (token.kind === 'name') return WORD_BINARY[token.value.toLowerCase()];
  return undefined;
}

const FUNCTIONS: Record<string, { arity: number | 'any'; apply: (args: number[]) => number }> = {
  MIN: { arity: 'any', apply: (a) => Math.min(...a) },
  MAX: { arity: 'any', apply: (a) => Math.max(...a) },
  ABS: { arity: 1, apply: ([a]) => Math.abs(a!) },
  ROUND: { arity: 2, apply: ([a, b]) => Number(a!.toFixed(Math.max(0, Math.trunc(b!)))) },
  SQRT: { arity: 1, apply: ([a]) => Math.sqrt(a!) },
  // `IF(test, then, else)` — a test of "greater than zero", which is all the
  // comparison this language has room for. Anything richer belongs in a cell.
  IF: { arity: 3, apply: ([t, a, b]) => (t! > 0 ? a! : b!) },
};

export function compileExpression(src: string): Compiled {
  const tokens = tokenize(src);
  if (tokens.length === 0) throw new ExpressionError('The expression is empty.');

  let pos = 0;
  const fields = new Set<string>();

  const peek = (): Token | undefined => tokens[pos];
  const next = (): Token | undefined => tokens[pos++];

  function parseExpression(minPrecedence: number): Node {
    let left = parseUnary();
    for (;;) {
      const token = peek();
      const op = binaryOpAt(token);
      if (!op || op.precedence < minPrecedence) break;
      pos += 1;
      const right = parseExpression(op.rightAssoc ? op.precedence : op.precedence + 1);
      const l = left;
      left = (vars) => {
        const a = l(vars);
        const b = right(vars);
        if (a === null || b === null) return null;
        const value = op.apply(a, b);
        // A division by zero is a missing answer, not Infinity — Infinity would
        // propagate silently into a total and look like a very large number.
        return Number.isFinite(value) ? value : null;
      };
    }
    return left;
  }

  /**
   * `cond ? a : b`, right-associative like C and Excel's nested IFs — so
   * `a ? b : c ? d : e` reads as `a ? b : (c ? d : e)`. Sits ABOVE every binary
   * operator: `or`'s precedence (1) is the lowest `parseExpression` itself
   * reaches, and the ternary wraps around a whole such expression on each side.
   */
  function parseTernary(): Node {
    const cond = parseExpression(1);
    const q = peek();
    if (!(q?.kind === 'op' && q.value === '?')) return cond;
    pos += 1;
    const whenTrue = parseTernary();
    const colon = next();
    if (!colon || colon.kind !== 'op' || colon.value !== ':') {
      throw new ExpressionError('A "?" is missing its ":".');
    }
    const whenFalse = parseTernary();
    return (vars) => {
      const c = cond(vars);
      if (c === null) return null;
      return truthy(c) ? whenTrue(vars) : whenFalse(vars);
    };
  }

  function parseUnary(): Node {
    const token = peek();
    if (token?.kind === 'op' && (token.value === '-' || token.value === '+')) {
      pos += 1;
      const operand = parseUnary();
      const sign = token.value === '-' ? -1 : 1;
      return (vars) => {
        const v = operand(vars);
        return v === null ? null : sign * v;
      };
    }
    // Caught here, before parsePrimary would otherwise read it as a field
    // named "not" — same reserved-word trade as `and`/`or`.
    if (token?.kind === 'name' && token.value.toLowerCase() === 'not') {
      pos += 1;
      const operand = parseUnary();
      return (vars) => {
        const v = operand(vars);
        return v === null ? null : bool(!truthy(v));
      };
    }
    return parsePrimary();
  }

  function parsePrimary(): Node {
    const token = next();
    if (!token) throw new ExpressionError('The expression ends before it is finished.');

    if (token.kind === 'number') {
      const value = token.value;
      return () => value;
    }

    if (token.kind === 'paren' && token.value === '(') {
      const inner = parseTernary();
      const close = next();
      if (!close || close.kind !== 'paren' || close.value !== ')') {
        throw new ExpressionError('A "(" is missing its ")".');
      }
      return inner;
    }

    if (token.kind === 'name') {
      const fn = FUNCTIONS[token.value.toUpperCase()];
      if (fn && peek()?.kind === 'paren') {
        pos += 1; // the '('
        const args: Node[] = [];
        if (!(peek()?.kind === 'paren' && (peek() as { value: string }).value === ')')) {
          for (;;) {
            args.push(parseTernary());
            const sep = peek();
            if (sep?.kind === 'comma') {
              pos += 1;
              continue;
            }
            break;
          }
        }
        const close = next();
        if (!close || close.kind !== 'paren' || close.value !== ')') {
          throw new ExpressionError(`${token.value} is missing its ")".`);
        }
        if (fn.arity !== 'any' && args.length !== fn.arity) {
          throw new ExpressionError(`${token.value.toUpperCase()} takes ${fn.arity} arguments.`);
        }
        return (vars) => {
          const values: number[] = [];
          for (const arg of args) {
            const v = arg(vars);
            if (v === null) return null;
            values.push(v);
          }
          const out = fn.apply(values);
          return Number.isFinite(out) ? out : null;
        };
      }

      const name = token.value;
      fields.add(name);
      return (vars) => vars(name);
    }

    throw new ExpressionError(`Did not expect "${describe(token)}" here.`);
  }

  const root = parseTernary();
  if (pos < tokens.length) {
    throw new ExpressionError(`Did not expect "${describe(tokens[pos]!)}" after the expression.`);
  }

  const compiled = ((vars: Vars) => root(vars)) as Compiled;
  compiled.fields = [...fields];
  return compiled;
}

// ---------------------------------------------------------------------------
// Translating to Giraffe's own measure shape
// ---------------------------------------------------------------------------

export interface MeasureFormula {
  /** A tiny arithmetic formula over component LETTERS — Giraffe's own shape. */
  formula: string;
  /** One field per letter, in letter order (`fields[0]` is `A`, and so on). */
  fields: string[];
}

/**
 * `A, B, C, D, F, G, …` — Giraffe's own letters, skipping `E` because it is
 * read as Euler's number in its formula grammar, not confirmed here but by its
 * own host (see the investigation this brief required before any of this was
 * written).
 */
const GIRAFFE_LETTERS = 'ABCDFGHIJKLMNOPQRSTUVWXYZ';

/** This language's functions, spelled the way Giraffe's own grammar expects. */
const GIRAFFE_FUNCTION_NAMES: Record<string, string> = {
  MIN: 'min',
  MAX: 'max',
  ABS: 'abs',
  ROUND: 'round',
  SQRT: 'sqrt',
};

/**
 * Translate a gTables expression into Giraffe's own measure shape: a formula
 * over component LETTERS, plus which field each letter names, in letter order
 * — exactly what a measure's `formula`/`components` carry. See
 * `giraffe/analyticsMeasures.ts` for where this is used and why the reverse
 * direction (a Giraffe measure back into one of these strings) is only
 * attempted for the straightforward case.
 *
 * Reuses `compileExpression` to validate and to collect field order, then
 * re-walks the same token stream a second time to emit text instead of a
 * closure — `substituteNames`, below, mirrors every parse function here
 * exactly, so the two cannot disagree about what the grammar accepts or how
 * it groups.
 */
export function toMeasureFormula(src: string): MeasureFormula {
  const compiled = compileExpression(src);
  const fields = compiled.fields;
  if (fields.length > GIRAFFE_LETTERS.length) {
    throw new ExpressionError('This expression uses more distinct fields than a Giraffe measure can carry.');
  }
  const letterOf = new Map(fields.map((f, i) => [f, GIRAFFE_LETTERS[i]!]));
  return { formula: substituteNames(src, letterOf), fields };
}

function opText(token: Token): string {
  return token.kind === 'name' ? token.value.toLowerCase() : (token as { value: string }).value;
}

/**
 * The same grammar as `compileExpression`, walked a second time to emit
 * Giraffe-flavoured text instead of an evaluator closure. Every binary,
 * unary and ternary node is fully parenthesised on the way out — correct
 * regardless of what Giraffe's own precedence happens to be, and already the
 * house style its own formulas use (see the samples this translation was
 * checked against).
 *
 * Never called on text `compileExpression` has not already accepted, so every
 * `!`-asserted `next()` here is a token that validation already guaranteed is
 * there.
 */
export function substituteNames(src: string, letterOf: ReadonlyMap<string, string>): string {
  const tokens = tokenize(src);
  let pos = 0;
  const peek = (): Token | undefined => tokens[pos];
  const next = (): Token => tokens[pos++]!;

  function renderTernary(): string {
    const cond = renderExpr(1);
    const q = peek();
    if (!(q?.kind === 'op' && q.value === '?')) return cond;
    pos += 1;
    const whenTrue = renderTernary();
    pos += 1; // ':'
    const whenFalse = renderTernary();
    return `(${cond}?${whenTrue}:${whenFalse})`;
  }

  function renderExpr(minPrecedence: number): string {
    let left = renderUnary();
    for (;;) {
      const token = peek();
      const op = binaryOpAt(token);
      if (!op || op.precedence < minPrecedence) break;
      const opToken = next();
      const right = renderExpr(op.rightAssoc ? op.precedence : op.precedence + 1);
      left = `(${left}${opText(opToken)}${right})`;
    }
    return left;
  }

  function renderUnary(): string {
    const token = peek();
    if (token?.kind === 'op' && (token.value === '-' || token.value === '+')) {
      pos += 1;
      return `${token.value}${renderUnary()}`;
    }
    if (token?.kind === 'name' && token.value.toLowerCase() === 'not') {
      pos += 1;
      return `not(${renderUnary()})`;
    }
    return renderPrimary();
  }

  function renderPrimary(): string {
    const token = next();
    if (token.kind === 'number') return String(token.value);

    if (token.kind === 'paren' && token.value === '(') {
      const inner = renderTernary();
      pos += 1; // ')'
      return `(${inner})`;
    }

    // 'name' is the only case left — compileExpression already rejected
    // anything else reaching a primary position.
    if (token.kind !== 'name') throw new ExpressionError('Internal: unexpected token while rendering.');
    const upper = token.value.toUpperCase();
    const fn = FUNCTIONS[upper];
    if (fn && peek()?.kind === 'paren') {
      pos += 1; // '('
      const args: string[] = [];
      if (!(peek()?.kind === 'paren' && (peek() as { value: string }).value === ')')) {
        for (;;) {
          args.push(renderTernary());
          if (peek()?.kind === 'comma') {
            pos += 1;
            continue;
          }
          break;
        }
      }
      pos += 1; // ')'
      // IF has no equivalent function in Giraffe's grammar — it IS the ternary.
      if (upper === 'IF') return `(${args[0]}?${args[1]}:${args[2]})`;
      return `${GIRAFFE_FUNCTION_NAMES[upper] ?? upper.toLowerCase()}(${args.join(',')})`;
    }

    return letterOf.get(token.value)!;
  }

  return renderTernary();
}

/**
 * Translate a gTables expression into a plain Excel-compatible arithmetic
 * formula (no leading `=`), substituting each field for whatever `cellRef`
 * gives it — a `Features!I2`-style reference, for the Measures tab's own
 * cells: real formulas, never a `GI.*` call, so they recalculate like
 * anything else when Features changes.
 *
 * Comparisons become Excel's own `=`/`<>`; `and`/`or`/`not` become
 * `AND()`/`OR()`/the Excel-native `IF`-as-not trick; the ternary becomes
 * `IF(...)`. Every condition is wrapped `(...)>0` first, so Excel's own
 * truthiness (any nonzero number) cannot disagree with this language's
 * (`n>0`) on a negative number.
 */
export function toExcelFormula(src: string, cellRef: (field: string) => string): string {
  compileExpression(src); // validate first — same contract as toMeasureFormula
  return renderExcel(src, cellRef);
}

const EXCEL_COMPARISON: Record<string, string> = { '==': '=', '!=': '<>' };

function renderExcel(src: string, cellRef: (field: string) => string): string {
  const tokens = tokenize(src);
  let pos = 0;
  const peek = (): Token | undefined => tokens[pos];
  const next = (): Token => tokens[pos++]!;

  function ternary(): string {
    const cond = expr(1);
    const q = peek();
    if (!(q?.kind === 'op' && q.value === '?')) return cond;
    pos += 1;
    const whenTrue = ternary();
    pos += 1; // ':'
    const whenFalse = ternary();
    return `IF((${cond})>0,${whenTrue},${whenFalse})`;
  }

  function expr(minPrecedence: number): string {
    let left = unary();
    for (;;) {
      const token = peek();
      const op = binaryOpAt(token);
      if (!op || op.precedence < minPrecedence) break;
      const opToken = next();
      const right = expr(op.rightAssoc ? op.precedence : op.precedence + 1);
      if (opToken.kind === 'name') {
        // and/or — Excel has no infix spelling, only the functions.
        left = `${opToken.value.toLowerCase() === 'and' ? 'AND' : 'OR'}((${left})>0,(${right})>0)`;
      } else if (opToken.kind === 'op') {
        left = `(${left}${EXCEL_COMPARISON[opToken.value] ?? opToken.value}${right})`;
      }
    }
    return left;
  }

  function unary(): string {
    const token = peek();
    if (token?.kind === 'op' && (token.value === '-' || token.value === '+')) {
      pos += 1;
      return `${token.value}${unary()}`;
    }
    if (token?.kind === 'name' && token.value.toLowerCase() === 'not') {
      pos += 1;
      return `IF((${unary()})>0,0,1)`;
    }
    return primary();
  }

  function primary(): string {
    const token = next();
    if (token.kind === 'number') return String(token.value);

    if (token.kind === 'paren' && token.value === '(') {
      const inner = ternary();
      pos += 1; // ')'
      return `(${inner})`;
    }

    if (token.kind !== 'name') throw new ExpressionError('Internal: unexpected token while rendering.');
    const upper = token.value.toUpperCase();
    const fn = FUNCTIONS[upper];
    if (fn && peek()?.kind === 'paren') {
      pos += 1; // '('
      const args: string[] = [];
      if (!(peek()?.kind === 'paren' && (peek() as { value: string }).value === ')')) {
        for (;;) {
          args.push(ternary());
          if (peek()?.kind === 'comma') {
            pos += 1;
            continue;
          }
          break;
        }
      }
      pos += 1; // ')'
      // MIN/MAX/ABS/ROUND/SQRT/IF are already Excel's own spelling.
      if (upper === 'IF') return `IF((${args[0]})>0,${args[1]},${args[2]})`;
      return `${upper}(${args.join(',')})`;
    }

    return cellRef(token.value);
  }

  return ternary();
}

function describe(token: Token): string {
  switch (token.kind) {
    case 'number':
      return String(token.value);
    case 'name':
      return token.value;
    case 'comma':
      return ',';
    default:
      return token.value;
  }
}
